<template>
  <!-- The `dropdown` theme, not `info-tooltip`: this is an interactive menu, not
       a tooltip. The tooltip theme paints a dark slate surface, which made the
       emoji hover ($n1) and selected ($p0/$p6) states — both light-surface
       colors — render as near-identical white squares. `dropdown` is the
       library's light elevated card. Placement is pinned to top because the
       theme's own default is bottom. -->
  <VDropdown
    ref="dropdown"
    theme="dropdown"
    placement="top"
    :triggers="['click']"
    :popperTriggers="['click']"
    :autoHide="true"
  >
    <button
      type="button"
      class="reaction-picker-trigger"
      data-cy="reaction-picker-trigger"
      :disabled="loading"
      aria-label="Add reaction"
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="reaction-picker-icon">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
      </svg>
    </button>
    <template v-slot:popper>
      <div class="reaction-picker">
        <div class="reaction-picker__grid">
          <button
            v-for="emoji in commonEmojis"
            :key="emoji"
            type="button"
            class="reaction-picker__emoji"
            data-cy="reaction-emoji"
            :class="{ 'reaction-picker__emoji--selected': userReactions[emoji] }"
            @click="selectEmoji(emoji)"
            :aria-label="userReactions[emoji] ? `Remove ${emoji} reaction` : `React with ${emoji}`"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
    </template>
  </VDropdown>
</template>

<script>
export default {
  name: 'reaction-picker',
  props: {
    messageId: {
      type: Number,
      required: true,
    },
    userReactions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      // Apple's Tapback set, and deliberately the whole set on web. Native gets a
      // custom-emoji option because the OS hands it an emoji keyboard; a web
      // free-text field can't, so it only works for people who know their OS
      // shortcut. Web still isn't cut off from custom emoji — the API accepts any
      // emoji, and reaction_bubble's toggle lets a web user +1 whatever a native
      // client introduces.
      commonEmojis: ['❤️', '👍', '👎', '😂', '‼️', '❓'],
    };
  },
  methods: {
    selectEmoji(emoji) {
      if (this.loading) return;

      // Check if user already reacted with this emoji - if so, remove it
      const existingReactionId = this.userReactions[emoji];
      if (existingReactionId) {
        this.removeReaction(emoji, existingReactionId);
      } else {
        this.addReaction(emoji);
      }
    },
    addReaction(emoji) {
      this.loading = true;
      const url = this.$apiService.messageReactionsUrl(this.messageId);
      const params = {
        message_reaction: {
          emoji,
        },
      };

      axios.post(url, params)
        .then((response) => {
          this.loading = false;
          this.$emit('reaction-added', {
            emoji,
            reactionId: response.data.id,
          });
          this.closeDropdown();
        })
        .catch((error) => {
          this.loading = false;
          const errorMessage = error.response?.data?.errors?.[0] || 'Unable to add reaction';
          this.$notificationManager.emit('show-toast', errorMessage, false);
        });
    },
    removeReaction(emoji, reactionId) {
      this.loading = true;
      const url = this.$apiService.messageReactionUrl(this.messageId, reactionId);

      axios.delete(url)
        .then(() => {
          this.loading = false;
          this.$emit('reaction-removed', { emoji });
          this.closeDropdown();
        })
        .catch((error) => {
          this.loading = false;
          const errorMessage = error.response?.data?.errors?.[0] || 'Unable to remove reaction';
          this.$notificationManager.emit('show-toast', errorMessage, false);
        });
    },
    closeDropdown() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.hide();
      }
    },
  },
};
</script>
